import { Icon, Tooltip, useParsedSearchParams } from "@fidelix/fx-miranda";
import { styled, useTheme } from "styled-components";
import { DateTime } from "luxon";
import { Button } from "react-aria-components";
import { t } from "@lingui/core/macro";
import { getActiveRoute } from "../../common/nav-bar";
import { defaultReplayTimePeriodMillis } from "./history-replay-context";
import { useGraphicsContext } from "../graphics-context";
import {
  removeMillisFromDate,
  resetGraphicsValuesCache,
} from "./history-replay.utils";

type Props = {
  replayContainerHeight: number;
  resetContainerHeight: () => void;
};
export function ToggleHistoryReplayButton({
  replayContainerHeight,
  resetContainerHeight,
}: Props) {
  const theme = useTheme();
  const { parsedParams, setSearchParams } = useParsedSearchParams({
    replay: { parse: (p) => p.get("replay") === "true" },
    from: { type: "string", defaultValue: undefined },
    to: { type: "string", defaultValue: undefined },
  });
  const { element, cachedDataService } = useGraphicsContext();

  // NOTE/HACK: Use element height to determine that the graphics picture has actually loaded
  // This is used for the button rendering purposes - only render this button when the picture exists
  // TODO: Maybe a callback/variable in Margaret to determine when picture has actually loaded?
  if (!element || (element && element?.offsetHeight < 50)) {
    return null;
  }

  const onToggleReplayMode = () => {
    setSearchParams(
      (params) => {
        params.set("replay", !parsedParams.replay ? "true" : "false");
        element.setOverrideTime(undefined);
        element.setNoData([]);
        resetGraphicsValuesCache();

        if (!parsedParams.replay) {
          resetContainerHeight();
          params.set(
            "from",
            removeMillisFromDate(
              DateTime.fromMillis(
                DateTime.now().toMillis() - defaultReplayTimePeriodMillis,
              ),
            ).toISO(),
          );
          params.set("to", removeMillisFromDate(DateTime.now()).toISO());
        }
      },
      { replace: true },
    );
    cachedDataService?.resetState();
  };

  const activeRoute = getActiveRoute(["graphics", "points", "alarms"]);
  const isPointsView = activeRoute === "points" || activeRoute === "alarms";

  const tooltipText = parsedParams.replay
    ? t`Close history mode`
    : t`Open the graphic view in history mode`;

  return (
    <div style={{ position: "relative" }}>
      <Tooltip content={tooltipText}>
        <HistoryReplayButton
          style={{
            bottom:
              theme.spaceNumber("small") +
              (parsedParams.replay ? replayContainerHeight + 17 : 5),
          }}
          onPress={onToggleReplayMode}
          isReplayMode={parsedParams.replay}
          isPointsView={isPointsView}
        >
          <Icon icon="history" />
        </HistoryReplayButton>
      </Tooltip>
    </div>
  );
}

const HistoryReplayButton = styled(Button)<{
  isReplayMode: boolean;
  isPointsView: boolean;
}>`
  border: 1px solid ${(p) => p.theme.colors.lightNeutral};
  background: ${(p) =>
    p.isReplayMode
      ? p.theme.colors.neutralBackground
      : p.theme.colors.mutedNeutralBackground};
  box-shadow: 0px 4px 4px ${(p) => p.theme.colors.black}26;
  border-radius: 8px;
  width: 54px;
  height: 54px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  right: 150px;
  top: -59px;
  z-index: 50;
`;
